import React from 'react';
import { ArrowLeft, ShieldCheck, Printer, AlertTriangle, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// 高发诈骗类型速查（依据公安部国家反诈中心公开宣传口径整理）
const scamTypes = [
  {
    name: '冒充公检法',
    trick: '自称警察/检察官，称你涉嫌洗钱、拐卖等重案，要求把资金转入"安全账户"配合调查。',
    sign: '公检法机关不会通过电话、QQ、微信办案，更不存在"安全账户"。'
  },
  {
    name: '刷单返利',
    trick: '以"动动手指日赚几百"为饵，先小额返利博取信任，随后诱导大额垫付连单。',
    sign: '所有要求垫资的网络刷单都是诈骗，刷单本身也属违法行为。'
  },
  {
    name: '网络交友诱导投资（杀猪盘）',
    trick: '网恋交友培养感情后，引导你在虚假平台投资、博彩，前期小赚，后期无法提现。',
    sign: '凡是网友热情推荐"稳赚不赔"的投资理财平台，都是诈骗。'
  },
  {
    name: '冒充电商物流客服',
    trick: '谎称商品质量问题、快递丢失，主动"双倍理赔"，诱导你在假页面填银行卡、验证码。',
    sign: '退款理赔不需要你先转账或提供验证码，请一律在官方App内核实。'
  },
  {
    name: 'AI换脸与拟声诈骗',
    trick: '用AI合成亲友的声音、面孔，视频或语音"借钱""要求紧急转账"。',
    sign: '涉及转账，务必当面或多渠道核实身份（提问只有本人知道的事）。'
  },
  {
    name: '虚假网络贷款 / 注销校园贷',
    trick: '以"无抵押低息贷款"或"不注销校园贷影响征信"为由，收取保证金、解冻费。',
    sign: '放款前先收费的都是诈骗；征信不能人为修改，"注销校园贷"是伪命题。'
  },
  {
    name: '冒充领导熟人',
    trick: '盗用领导、亲友头像昵称添加好友，以帮忙周转、垫付合同款为由要求转账。',
    sign: '凡是涉及借钱转账，务必电话或当面核实本人。'
  },
  {
    name: '虚假购物服务',
    trick: '低价代购、海外奢侈品、游戏装备，收款后拉黑，或诱导脱离官方平台私下交易。',
    sign: '脱离正规平台交易没有保障，明显低于市场价要警惕。'
  },
  {
    name: 'ETC / 积分 / 快递异常短信钓鱼',
    trick: '短信称"ETC失效""积分即将清零""快递阳性理赔"，附钓鱼链接骗取银行卡信息。',
    sign: '不点击短信里的陌生链接，官方业务请在App或官网办理。'
  },
  {
    name: '屏幕共享类诈骗',
    trick: '冒充客服/公检法要求开启屏幕共享、"指导操作"，实时偷看你的密码与验证码。',
    sign: '任何客服都不会要求共享屏幕，共享 = 把银行卡交到对方手里。'
  }
];

// 防骗铁律（见到这类要求，基本可以直接判定为诈骗）
const ironRules = [
  '凡是要求转账到"安全账户"的，都是诈骗',
  '凡是网络刷单、点赞返佣的兼职，都是诈骗',
  '凡是索要短信验证码、银行卡密码的，都是诈骗',
  '凡是网友荐股、引导投资博彩平台的，都是诈骗',
  '凡是要求开启屏幕共享指导操作的，都是诈骗',
  '凡是放款前要先交保证金、解冻费的，都是诈骗',
  '凡是快递理赔要你先垫付的，都是诈骗',
  '凡是"公检法"电话里让你保密、不许告诉家人的，都是诈骗'
];

// 被骗后的黄金四步
const emergencySteps = [
  { step: '第一步', action: '立即拨打 110 或反诈专线 96110', detail: '时间就是金钱，越早报警止付成功率越高。' },
  { step: '第二步', action: '保留全部证据', detail: '聊天记录、转账凭证、对方账号、电话、网址链接，截图保存，不要删除。' },
  { step: '第三步', action: '联系银行 / 支付平台', detail: '申请紧急止付、冻结对方账户，说明遭遇电信诈骗请求协助。' },
  { step: '第四步', action: '尽快到就近派出所报案', detail: '携带证据原件当面报案，配合警方录入接警系统。' }
];

const AntiFraudManualPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 no-print">
        <div className="flex justify-between items-center max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回主页
          </button>
          <h1 className="text-xl font-bold flex items-center text-red-700">
            <ShieldCheck className="w-6 h-6 mr-2" />
            防骗速查手册
          </h1>
          <button
            onClick={() => window.print()}
            className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <Printer className="w-4 h-4 mr-1" />
            打印
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-10">
        {/* 铁律 */}
        <section>
          <h2 className="text-2xl font-bold text-red-700 mb-1 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2" />
            防骗铁律：见到即报警级警惕
          </h2>
          <p className="text-sm text-gray-500 mb-4">背下这几条，能避开 90% 的电信网络诈骗。</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {ironRules.map((rule, index) => (
              <div key={index} className="bg-white border-2 border-red-100 rounded-lg p-3 text-sm text-gray-700 shadow-sm">
                <span className="font-bold text-red-600 mr-1">铁律{index + 1}：</span>{rule}
              </div>
            ))}
          </div>
        </section>

        {/* 十大高发类型 */}
        <section>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">十大高发诈骗类型速查</h2>
          <p className="text-sm text-gray-500 mb-4">每一类的套路和识别要点，来自真实案例改编题库。</p>
          <div className="space-y-3">
            {scamTypes.map((item, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="font-bold text-gray-800 mb-2">
                  <span className="inline-block w-6 h-6 mr-2 text-center text-xs leading-6 bg-red-600 text-white rounded-full">
                    {index + 1}
                  </span>
                  {item.name}
                </h3>
                <p className="text-sm text-gray-600 mb-1.5">
                  <span className="font-medium text-gray-700">套路：</span>{item.trick}
                </p>
                <p className="text-sm text-emerald-700">
                  <span className="font-medium">识别要点：</span>{item.sign}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 被骗后怎么办 */}
        <section>
          <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center">
            <PhoneCall className="w-6 h-6 mr-2 text-red-600" />
            万一被骗：黄金四步
          </h2>
          <p className="text-sm text-gray-500 mb-4">被骗不可怕，可怕的是拖延和沉默。第一时间这样做：</p>
          <div className="space-y-3">
            {emergencySteps.map((item, index) => (
              <div key={index} className="bg-white rounded-xl border-2 border-amber-200 p-4 flex gap-3">
                <div className="shrink-0 w-16 text-center">
                  <span className="inline-block px-2 py-1 text-xs font-bold bg-amber-500 text-white rounded">
                    {item.step}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">{item.action}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 提示 */}
        <section className="bg-red-600 rounded-xl p-5 text-white text-center">
          <p className="font-bold text-lg mb-1">下载「国家反诈中心」App</p>
          <p className="text-sm text-red-100">
            开启来电预警，可自动识别诈骗电话与短信。记住反诈专线：<span className="font-bold text-white">96110</span>
          </p>
        </section>
      </main>
    </div>
  );
};

export default AntiFraudManualPage;
